import { useSearchParams } from "@solidjs/router";
import type { IconTypes } from "solid-icons";
import { TbOutlineExternalLink } from "solid-icons/tb";
import {
  createEffect,
  createUniqueId,
  type JSX,
  type ParentComponent,
  Show,
  splitProps,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { usePostHog } from "@/hooks/usePostHog";
import { cn } from "@/libs/cn";
import type { OnboardingStep } from "../../context/OnboardingContext";

type OnboardingReviewCardProps = {
  /** Leading icon shown in the rounded chip next to the title. */
  icon: IconTypes;
  title: string;
  /** Short contextual line under the title, e.g. "Step 4 · 5 esperienze". */
  subtitle: string;
  /** Step the "open step" action navigates to via the `edit` search param. */
  step: OnboardingStep;
  /**
   * A message from the backend about this section, shown under the title with
   * `role="alert"`. When it appears the card scrolls into view, since the
   * review is long and the message is the reason the freelancer is back here.
   */
  error?: string | null;
} & Omit<JSX.HTMLAttributes<HTMLElement>, "title">;

/**
 * Always-open review section used in the onboarding preview. Renders a header
 * (icon + title + subtitle + an explicit "open step" action that deep-links
 * back into the matching step) and an arbitrary body.
 */
export const OnboardingReviewCard: ParentComponent<
  OnboardingReviewCardProps
> = (props) => {
  const [local, rest] = splitProps(props, [
    "icon",
    "title",
    "subtitle",
    "step",
    "error",
    "children",
    "class",
    "classList",
  ]);
  const [, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const { capture } = usePostHog();

  const Icon = local.icon;
  const errorId = createUniqueId();
  let sectionRef: HTMLElement | undefined;

  // Also runs on mount: the review remounts after a failed submit with the
  // message already set.
  createEffect(() => {
    if (local.error) sectionRef?.scrollIntoView({ block: "center" });
  });

  const openStep = () => {
    capture("click_edit_review_section", { step: local.step });
    setSearchParams({ edit: local.step });
  };

  return (
    <section
      ref={sectionRef}
      class={cn(
        "flex flex-col rounded-2xl border border-neutral-200/70 bg-white shadow-sm",
        local.class,
      )}
      classList={local.classList}
      {...rest}
    >
      <header class="flex items-center gap-3 px-5 py-4">
        <span class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon size={20} />
        </span>
        <div class="flex min-w-0 flex-col">
          <h3 class="text-base font-semibold text-neutral-900">
            {local.title}
          </h3>
          <p class="truncate text-sm text-neutral-400">{local.subtitle}</p>
          <Show when={local.error}>
            {(message) => (
              <p
                id={errorId}
                role="alert"
                class="mt-1 text-sm text-red-700 wrap-break-word"
              >
                {message()}
              </p>
            )}
          </Show>
        </div>
        <button
          type="button"
          onClick={openStep}
          aria-label={`${t("onboarding.steps.preview.openStep")} – ${local.title}`}
          aria-describedby={local.error ? errorId : undefined}
          class="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <TbOutlineExternalLink size={16} />
          {t("onboarding.steps.preview.openStep")}
        </button>
      </header>
      <div class="border-t border-neutral-100 px-5 py-4">{local.children}</div>
    </section>
  );
};

/** Wraps a set of label/value rows with consistent dividers. */
export const OnboardingReviewFieldList: ParentComponent = (props) => (
  <dl class="divide-y divide-neutral-100">{props.children}</dl>
);

type OnboardingReviewFieldProps = {
  label: string;
  /** Rendered when no value is provided. Defaults to an em dash. */
  fallback?: JSX.Element;
};

/** A single label/value row. Shows a muted fallback when empty. */
export const OnboardingReviewField: ParentComponent<
  OnboardingReviewFieldProps
> = (props) => (
  <div class="flex items-baseline gap-4 py-3 first:pt-0 last:pb-0">
    <dt class="w-36 shrink-0 text-sm text-neutral-400">{props.label}</dt>
    <dd class="min-w-0 break-words text-sm font-medium text-neutral-800">
      <Show
        when={props.children}
        fallback={<span class="text-neutral-300">{props.fallback ?? "—"}</span>}
      >
        {props.children}
      </Show>
    </dd>
  </div>
);

type OnboardingReviewListItemProps = {
  /** Leading visual: an avatar with initials or a section icon. */
  leading: JSX.Element;
  title: string;
  subtitle: string;
};

/** A row inside list-style sections (experience, education). */
export const OnboardingReviewListItem: ParentComponent<
  OnboardingReviewListItemProps
> = (props) => (
  <div class="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
    <span class="shrink-0">{props.leading}</span>
    <div class="flex min-w-0 flex-col">
      <span class="truncate text-sm font-semibold text-neutral-900">
        {props.title}
      </span>
      <span class="truncate text-sm text-neutral-400">{props.subtitle}</span>
    </div>
  </div>
);

/** Renders up to two uppercase initials derived from a name. */
export const OnboardingReviewAvatar = (props: { name: string }) => {
  const initials = () =>
    props.name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? "")
      .join("");
  return (
    <span class="flex size-9 items-center justify-center rounded-lg bg-neutral-100 text-xs font-semibold text-neutral-500">
      {initials()}
    </span>
  );
};

/** A square icon chip used as the leading visual for icon-based list rows. */
export const OnboardingReviewIconAvatar: ParentComponent = (props) => (
  <span class="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
    {props.children}
  </span>
);
